"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { SmartTicketDisplay } from "@/components/shared/smart-display/SmartTicketDisplay";
import { SmartUserDisplay } from "@/components/shared/smart-display/SmartUserDisplay";
import { EmailAliasesEditor } from "@/features/contacts/components/EmailAliasesEditor";
import { PhoneAliasesEditor } from "@/features/contacts/components/PhoneAliasesEditor";
import { TagsFieldDisplay } from "@/components/shared/TagsFieldDisplay/TagsFieldDisplay";
import { ListFieldEditor } from "@/components/shared/ListFieldEditor/ListFieldEditor";
import { AssignTicketToUserSelect } from "@/features/tickets/components/AssignTicketToUserSelect";
import { renderCellValue } from "../../UniversalCRMTable/utils";
import { linkifyText } from "@/lib/utils/linkify-text";
import { renderFieldInput } from "../../UniversalCRMTable/utils/field-input-renderer";
import type { SimpleFieldConfig, SimpleTableConfig, BaseEntity } from "../../UniversalCRMTable/types";
import type { TagsFieldValue, ContactLike } from "@/lib/types/entity-page-types";
import type { PhoneAliasesEditable } from "@/features/contacts/components/phone-aliases-utils";

// ============================================================================
// FIELD RENDERING HELPERS
// ============================================================================

export interface FieldRenderingHelpersProps<T extends BaseEntity> {
  item: T;
  editingField: string | null;
  onFieldValueChange: (fieldId: string, newValue: unknown) => void;
}

export function renderFieldInputWithCustomHandling<T extends BaseEntity>(
  fieldId: string,
  fieldConfig: SimpleFieldConfig,
  value: unknown,
  item: T,
  onFieldValueChange: (fieldId: string, newValue: unknown) => void,
  onSaveField?: (fieldId: string) => Promise<void>,
  config?: SimpleTableConfig<T>,
  onKeyDown?: (e: React.KeyboardEvent) => void
): React.ReactNode {
  // Special handling for tags field - use ListFieldEditor
  // When ListFieldEditor saves, automatically trigger the parent's save
  if (fieldConfig.type === "tags") {
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Click the edit button to manage tags. Changes will be saved automatically.
        </p>
        <ListFieldEditor
          value={value as TagsFieldValue}
          fieldId={fieldId}
          fieldLabel={fieldConfig.label || fieldId}
          globalModalId="list-editor-tags"
          onSave={async (newValue) => {
            // Update the field value in the parent component
            onFieldValueChange(fieldId, newValue);

            // Automatically trigger the parent's save to persist to database
            if (onSaveField) {
              await onSaveField(fieldId);
            }
            return Promise.resolve();
          }}
          placeholder="Enter tag and press Add"
          maxItems={20}
        />
      </div>
    );
  }

  // Special handling for email_aliases component
  if (fieldId === "email_aliases" && (item as unknown as { email?: string })?.email) {
    const contactLike: ContactLike = {
      id: (item as unknown as { id: string }).id,
      email: (item as unknown as { email: string }).email,
      email_aliases: (item as unknown as { email_aliases?: string[] })?.email_aliases,
    };
    return (
      <EmailAliasesEditor
        contact={contactLike}
        onUpdate={(updated) => {
          onFieldValueChange(fieldId, updated.email_aliases);
        }}
        isEditing
        onEditChange={() => {}}
      />
    );
  }

  // Special handling for phone_aliases component
  if (fieldId === "phone_aliases" && (item as unknown as { id?: string })?.id) {
    const contactLike: PhoneAliasesEditable = {
      id: (item as unknown as { id: string }).id,
      phone: (item as unknown as { phone?: string | null })?.phone,
      phone_aliases: (item as unknown as { phone_aliases?: string[] })?.phone_aliases,
    };
    return (
      <PhoneAliasesEditor
        contact={contactLike}
        onUpdate={(updated) => {
          onFieldValueChange(fieldId, updated.phone_aliases);
        }}
        isEditing
        onEditChange={() => {}}
      />
    );
  }

  // Ticket assigned_to: use AssignTicketToUserSelect with expandInline (avoids popover in modal)
  const entity = config?.entity;
  if (entity === "tickets" && fieldId === "assigned_to" && item?.id) {
    return (
      <AssignTicketToUserSelect
        ticketId={String(item.id)}
        currentUserId={value != null ? String(value) : undefined}
        expandInline
        onSuccessWithValue={(newUserId) => {
          onFieldValueChange(fieldId, newUserId);
          void onSaveField?.(fieldId);
        }}
        placeholder="Assign to..."
        className="w-full"
      />
    );
  }

  // Use the shared field input renderer for all other field types (expand inline in panel/modal)
  return renderFieldInput({
    fieldId,
    fieldConfig,
    value: (value ?? null) as string | number | boolean | null,
    onValueChange: (newValue) => onFieldValueChange(fieldId, newValue),
    placeholder: fieldConfig.placeholder,
    fullWidth: true,
    autoFocus: true,
    expandInline: true,
    onKeyDown,
  });
}

export function renderFieldDisplay<T extends BaseEntity>(
  fieldConfig: SimpleFieldConfig,
  value: unknown,
  fieldId: string | undefined,
  item: T,
  editingField: string | null,
  entityType?: string
): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-gray-400 italic">Not set</span>;
  }
  const isFieldEditing = editingField === fieldId;

  if (fieldId === "ticket_id" || (fieldId != null && fieldId.includes("ticket"))) {
    const ticketId =
      typeof value === "object" && value !== null && "id" in value
        ? (value as { id: string }).id
        : value;
    return (
      <div className="text-sm text-card-foreground">
        <SmartTicketDisplay
          ticketId={String(ticketId)}
          showIcon
          className="inline-block"
          disabled={isFieldEditing}
        />
      </div>
    );
  }

  if (fieldConfig.type === "multi-user") {
    const arr = Array.isArray(value) ? value : [];
    if (arr.length > 0) {
      return (
        <div className="flex gap-1 flex-wrap">
          {arr.slice(0, 3).map((userItem: unknown, index: number) => {
            if (typeof userItem === "object" && userItem !== null && "id" in userItem) {
              const u = userItem as { id: string };
              return (
                <SmartUserDisplay
                  key={index}
                  userId={u.id}
                  userData={u}
                  showIcon
                  className="inline-block"
                  disabled={isFieldEditing}
                />
              );
            }
            return (
              <SmartUserDisplay
                key={index}
                userId={String(userItem)}
                showIcon
                className="inline-block"
                disabled={isFieldEditing}
              />
            );
          })}
          {arr.length > 3 && (
            <Badge variant="secondary" className="text-xs">
              +{arr.length - 3}
            </Badge>
          )}
        </div>
      );
    }
    return (
      <SmartUserDisplay
        userId={String(value)}
        showIcon
        className="inline-block"
        disabled={isFieldEditing}
      />
    );
  }

  if (fieldConfig.type === "tags") {
    return (
      <TagsFieldDisplay value={value as TagsFieldValue} fieldId={fieldId || ""} maxItems={5} />
    );
  }

  if (fieldId === "email_aliases" && (item as unknown as { email?: string })?.email) {
    // Type assertion: item should be a Contact-like object with email
    const contactLike: ContactLike = {
      id: (item as unknown as { id: string }).id,
      email: (item as unknown as { email: string }).email,
      email_aliases: (item as unknown as { email_aliases?: string[] })?.email_aliases,
    };
    return (
      <EmailAliasesEditor
        contact={contactLike}
        onUpdate={() => {}}
        isEditing={false}
        onEditChange={() => {}}
      />
    );
  }

  if (fieldId === "phone_aliases" && (item as unknown as { id?: string })?.id) {
    const contactLike: PhoneAliasesEditable = {
      id: (item as unknown as { id: string }).id,
      phone: (item as unknown as { phone?: string | null })?.phone,
      phone_aliases: (item as unknown as { phone_aliases?: string[] })?.phone_aliases,
    };
    return (
      <PhoneAliasesEditor
        contact={contactLike}
        onUpdate={() => {}}
        isEditing={false}
        onEditChange={() => {}}
      />
    );
  }

  // Long free-text (notes, descriptions, content): render http(s) URLs as
  // clickable links so pasted links in notes are usable straight from the detail
  // modal. XSS-safe (linkifyText emits React text nodes, no innerHTML).
  if (fieldConfig.type === "textarea" && typeof value === "string" && value.length > 0) {
    return (
      <span className="whitespace-pre-wrap break-words text-card-foreground">
        {linkifyText(value)}
      </span>
    );
  }

  // Handle case where value might be an empty object
  const safeValue =
    value && typeof value === "object" && Object.keys(value).length === 0 ? null : value;
  return renderCellValue(
    (safeValue ?? null) as string | number | boolean | null,
    fieldConfig,
    fieldId,
    isFieldEditing,
    {
      entityRecord: item as Record<string, unknown>,
      entityType,
    }
  );
}
